import { motion } from "framer-motion";
import { Pill, Camera, Activity, FolderHeart, Users, ShieldAlert } from "lucide-react";
import { siteContent } from "@/content/site";

const FEATURE_ICONS = {
  pill: Pill,
  camera: Camera,
  activity: Activity,
  folder: FolderHeart,
  users: Users,
  sos: ShieldAlert,
};

const LAYOUT = [
  { span: "md:col-span-4 lg:col-span-6", accent: "olive" },
  { span: "md:col-span-4 lg:col-span-6", accent: "terracotta" },
  { span: "md:col-span-4 lg:col-span-4", accent: "brand" },
  { span: "md:col-span-4 lg:col-span-4", accent: "olive" },
  { span: "md:col-span-4 lg:col-span-4", accent: "terracotta" },
  { span: "md:col-span-8 lg:col-span-12", accent: "brand", featured: true },
];

const ACCENTS = {
  brand: "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F]",
  olive: "bg-[#E1EEEA] text-[#0E2A36] border-[#C9DDD6]",
  terracotta: "bg-[#E8A33D] text-white border-[#E8A33D]",
  light: "bg-white text-[#0E2A36] border-[#C9DDD6]",
};

const { features } = siteContent;

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-20 lg:py-28 bg-[#F4FAF8]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8A33D]">
            {features.eyebrow}
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E2A36]">
            {features.headingLead}{" "}
            <em className="italic font-medium text-[#1F4E5F]">{features.headingItalic}</em>
          </h2>
          <p className="mt-4 text-[#3D5560] text-lg">{features.subhead}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5">
          {features.items.map((f, i) => {
            const layout = LAYOUT[i] || LAYOUT[LAYOUT.length - 1];
            const cls = ACCENTS[layout.accent] || ACCENTS.light;
            const isDark = layout.accent === "brand" || layout.accent === "terracotta";
            const Icon = FEATURE_ICONS[f.iconKey] || Pill;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                className={`relative ${layout.span} ${cls} border rounded-3xl p-7 lg:p-9 group hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(15,56,32,0.10)] transition-all duration-300 overflow-hidden`}
                data-testid={`feature-card-${i}`}
              >
                {layout.featured && (
                  <div className="absolute inset-0 grain opacity-60 pointer-events-none" />
                )}
                <div className="relative">
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${
                      isDark
                        ? "bg-white/10 text-current"
                        : "bg-[#1F4E5F] text-[#F4FAF8]"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display text-xl lg:text-2xl font-semibold mb-2 leading-tight">
                    {f.title}
                  </h3>
                  <p
                    className={`text-[14.5px] leading-relaxed ${
                      isDark ? "text-white/80" : "text-[#3D5560]"
                    } ${layout.featured ? "max-w-2xl" : ""}`}
                  >
                    {f.body}
                  </p>
                  <span
                    className={`inline-block mt-5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                      isDark ? "bg-white/15 text-white" : "bg-[#1F4E5F] text-[#F4FAF8]"
                    }`}
                  >
                    {features.badge}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
